import firebase from 'firebase/app'
import 'firebase/remote-config'
import { isMockApi } from '../mock/isMockApi'

const FEATURE_FLAG_TESTERS_KEY = 'feature_flag_testers'
const FEATURE_FLAGS_KEY = 'feature_flags'

export const FEATURE_STATES = {
  DISABLED: 'disabled',
  BETA: 'beta',
  ENABLED: 'enabled',
}

export const FEATURES = {
  AI_ASSISTANT: 'ai_assistant',
}

let remoteConfigInstance = null
let initPromise = null

const parseJsonArray = (raw) => {
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const parseFeatureFlags = (raw) => {
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const normalizeEntry = (entry) => String(entry).trim().toLowerCase()

const getRemoteConfigValue = (key, fallback) => {
  if (!remoteConfigInstance) return fallback
  return remoteConfigInstance.getValue(key).asString()
}

const getTestersAllowlist = () =>
  parseJsonArray(getRemoteConfigValue(FEATURE_FLAG_TESTERS_KEY, '[]'))

const getFeatureFlags = () =>
  parseFeatureFlags(getRemoteConfigValue(FEATURE_FLAGS_KEY, '{}'))

const matchesAllowlist = (user, allowlist) => {
  if (!allowlist.length) return false

  const { userid, email } = user
  const normalizedAllowlist = allowlist.map(normalizeEntry)

  if (userid && normalizedAllowlist.includes(normalizeEntry(userid))) {
    return true
  }

  if (email && normalizedAllowlist.includes(normalizeEntry(email))) {
    return true
  }

  return false
}

export const initFeatureFlags = () => {
  if (initPromise) return initPromise

  initPromise = (async () => {
    remoteConfigInstance = firebase.remoteConfig()
    remoteConfigInstance.defaultConfig = {
      [FEATURE_FLAG_TESTERS_KEY]: '[]',
      [FEATURE_FLAGS_KEY]: JSON.stringify({
        [FEATURES.AI_ASSISTANT]: FEATURE_STATES.DISABLED,
      }),
    }
    remoteConfigInstance.settings = {
      minimumFetchIntervalMillis:
        process.env.NODE_ENV === 'production' ? 3600000 : 0,
    }
    await remoteConfigInstance.fetchAndActivate()
    exposeDevDebugTools()
    return remoteConfigInstance
  })()

  return initPromise
}

export const refreshFeatureFlags = async () => {
  if (!remoteConfigInstance) return initFeatureFlags()
  await remoteConfigInstance.fetchAndActivate()
  return remoteConfigInstance
}

export const isFeatureFlagTester = (user = {}) =>
  matchesAllowlist(user, getTestersAllowlist())

export const isFeatureEnabled = (featureKey, user = {}) => {
  if (featureKey === FEATURES.AI_ASSISTANT && isMockApi()) {
    return true
  }

  const flags = getFeatureFlags()
  const state = flags[featureKey] || FEATURE_STATES.DISABLED

  switch (state) {
    case FEATURE_STATES.ENABLED:
      return true
    case FEATURE_STATES.BETA:
      return isFeatureFlagTester(user)
    case FEATURE_STATES.DISABLED:
    default:
      return false
  }
}

export const isAIAssistantEnabled = (user = {}) =>
  isFeatureEnabled(FEATURES.AI_ASSISTANT, user)

export const getFeatureFlagsDebugInfo = (user = {}) => ({
  testers: getTestersAllowlist(),
  flags: getFeatureFlags(),
  isTester: isFeatureFlagTester(user),
  features: Object.values(FEATURES).reduce((acc, featureKey) => {
    acc[featureKey] = isFeatureEnabled(featureKey, user)
    return acc
  }, {}),
  user: {
    userid: user.userid || null,
    email: user.email || null,
  },
})

const exposeDevDebugTools = () => {
  if (process.env.NODE_ENV === 'production') return

  window.__informedFeatureFlags = {
    FEATURES,
    FEATURE_STATES,
    getDebugInfo: getFeatureFlagsDebugInfo,
    isFeatureEnabled,
    isFeatureFlagTester,
    refresh: refreshFeatureFlags,
  }
}
